import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import {
  Container,
  Title,
  Alert,
  Text,
  Stack,
  Group,
  Card,
  Badge,
  Button,
  Loader,
  Paper,
  Divider,
  ActionIcon,
  Modal,
} from '@mantine/core';
import {
  IconRefresh,
  IconCheck,
  IconX,
  IconClock,
  IconAlertCircle,
  IconExclamationCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useLiveQuery, useApiClient, useSession } from '../../api';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUsd = (cents) => (cents == null ? '' : usd.format(cents / 100));

const MySpecialRequestsPage = () => {
  const navigate = useNavigate();
  const api = useApiClient();
  const { me } = useSession();

  // Requester-side live list: any request lifecycle event on the caller's
  // topic (accepted/rejected/countered/...) invalidates and refetches.
  const { data, loading, error, refetch } = useLiveQuery(
    '/api/v1/requests',
    {},
    { topics: [me && `user.${me.clerk_user_id}.requests`] }
  );
  const requests = data?.items || [];

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewCounterOfferModal, setViewCounterOfferModal] = useState(false);

  const handleAcceptCounterOffer = async (requestId) => {
    try {
      await api.post(`/api/v1/requests/${requestId}/accept-counter`);
      notifications.show({
        title: 'Counter offer accepted',
        message: 'You have accepted the princess\'s counter offer',
        color: 'green'
      });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to accept counter offer',
        color: 'red'
      });
    }
  };

  // Declining a counter offer as the requester means cancelling the request —
  // reject is a princess-only action (contract §4.6).
  const handleDeclineCounterOffer = async (requestId) => {
    try {
      await api.post(`/api/v1/requests/${requestId}/cancel`);
      notifications.show({
        title: 'Counter offer declined',
        message: 'You have declined the counter offer and cancelled the request',
        color: 'orange'
      });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to decline counter offer',
        color: 'red'
      });
    }
  };

  // An expired request is revived by creating a fresh one from the same
  // terms; the API cancels the original in the same transaction (§4.6).
  const handleResubmitRequest = async (requestId) => {
    try {
      await api.post(`/api/v1/requests/${requestId}/resubmit`);
      notifications.show({
        title: 'Request resubmitted',
        message: 'Your request was sent again with a fresh 72-hour window',
        color: 'green'
      });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to resubmit request',
        color: 'red'
      });
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await api.post(`/api/v1/requests/${requestId}/cancel`);
      notifications.show({
        title: 'Request cancelled',
        message: 'Your special request has been cancelled',
        color: 'gray'
      });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to cancel request',
        color: 'red'
      });
    }
  };

  // princess_id is a seller_profiles.id; the conversation route needs the
  // princess's users.id — resolve it via the public seller endpoint.
  const handleMessagePrincess = async (princessId) => {
    try {
      const seller = await api.get(`/api/v1/sellers/${princessId}`);
      navigate(`/messages/${seller.user_id}`);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Could not open the conversation',
        color: 'red'
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'yellow',
      accepted: 'green',
      counter_offered: 'pink',
      rejected: 'red',
      completed: 'teal',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const getStatusLabel = (status) =>
    status === 'completed' ? 'COMPLETED ✓' : status.replace('_', ' ').toUpperCase();

  // Expiry is not a status (§3.11) — the row keeps pending/counter_offered
  // and carries is_expired. To the buyer it reads as its own state, since a
  // lapsed request is no longer in the princess's queue.
  const formatCountdown = (expiresAt) => {
    const msLeft = new Date(expiresAt) - Date.now();
    if (msLeft <= 0) return null;
    const hours = Math.floor(msLeft / 3600000);
    if (hours >= 1) return `${hours}h left to respond`;
    return `${Math.max(1, Math.round(msLeft / 60000))}m left to respond`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewCounterOffer = (request) => {
    setSelectedRequest(request);
    setViewCounterOfferModal(true);
  };

  if (loading) {
    return (
      <ProtectedRoute requireAuth={true} >
        <Container size="lg" py="xl">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader size="lg" />
          </div>
        </Container>
      </ProtectedRoute>
    );
  }

  if (error?.code === 'forbidden') {
    return (
      <ProtectedRoute requireAuth={true} >
        <Container size="lg" py="xl">
          <Alert
            icon={<IconExclamationCircle size="1rem" />}
            color="yellow"
            title="PayPiggyPro Required"
          >
            <Stack>
              <Text>
                Special requests are only available to PayPiggyPro subscribers.
              </Text>
              <Button
                variant="gradient"
                gradient={{ from: 'pink', to: 'cyan' }}
                onClick={() => navigate('/subscription')}
              >
                Upgrade to PayPiggyPro
              </Button>
            </Stack>
          </Alert>
        </Container>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requireAuth={true} >
      <Container size="lg" py="xl">
        <Stack>
          <Group justify="space-between">
            <Title order={1}>My Special Requests</Title>
            <ActionIcon onClick={refetch} variant="light" size="lg">
              <IconRefresh size="1.2rem" />
            </ActionIcon>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {requests.length === 0 ? (
            <Paper p="xl" withBorder>
              <Stack align="center" spacing="md">
                <IconClock size={48} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed" ta="center" size="lg">
                  No special requests yet
                </Text>
                <Text c="dimmed" ta="center" size="sm">
                  Visit a princess's profile to make a special request
                </Text>
                <Button onClick={() => navigate('/princesses')}>
                  Browse Princesses
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Stack>
              {requests.map((request) => (
                <Card key={request.id} shadow="sm" p="lg" radius="md" withBorder>
                  <Stack>
                    <Group justify="space-between">
                      <Group>
                        <div>
                          <Text size="xs" c="dimmed">Requested from</Text>
                          <Text fw={500}>{request.princess_name}</Text>
                        </div>
                        <Badge
                          size="lg"
                          color={request.is_expired ? 'orange' : getStatusColor(request.status)}
                        >
                          {request.is_expired ? 'EXPIRED' : getStatusLabel(request.status)}
                        </Badge>
                      </Group>
                      <div>
                        <Text size="xs" c="dimmed">Submitted</Text>
                        <Text size="sm">{formatDate(request.created_at)}</Text>
                      </div>
                    </Group>

                    <Divider />

                    <Group grow>
                      <div>
                        <Text size="sm" c="dimmed">Service Type</Text>
                        <Text fw={500}>{request.service_type}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Polish Color</Text>
                        <Text fw={500}>{request.nail_polish_color || 'Not specified'}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Pictures</Text>
                        <Text fw={500}>{request.number_of_pictures}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Your Offer</Text>
                        <Text fw={600} size="lg" c="pink">{formatUsd(request.offer_price_cents)}</Text>
                      </div>
                    </Group>

                    {request.design && (
                      <div>
                        <Text size="sm" c="dimmed">Design Request</Text>
                        <Text>{request.design}</Text>
                      </div>
                    )}

                    {request.special_notes && (
                      <div>
                        <Text size="sm" c="dimmed">Special Notes</Text>
                        <Text>{request.special_notes}</Text>
                      </div>
                    )}

                    {request.status === 'counter_offered' && request.counter_offer && (
                      <Paper p="md" withBorder bg="pink.0">
                        <Stack>
                          <Group justify="space-between">
                            <Text fw={600} c="pink">Counter Offer from Princess</Text>
                            <Badge color="pink">NEW OFFER</Badge>
                          </Group>
                          <Divider />
                          <Group grow>
                            {request.counter_offer.offer_price_cents != null && (
                              <div>
                                <Text size="sm" c="dimmed">New Price</Text>
                                <Text fw={600} size="lg" c="pink">{formatUsd(request.counter_offer.offer_price_cents)}</Text>
                              </div>
                            )}
                            {request.counter_offer.number_of_pictures != null && (
                              <div>
                                <Text size="sm" c="dimmed">Pictures</Text>
                                <Text fw={500}>{request.counter_offer.number_of_pictures}</Text>
                              </div>
                            )}
                            {request.counter_offer.service_type && (
                              <div>
                                <Text size="sm" c="dimmed">Service Type</Text>
                                <Text fw={500}>{request.counter_offer.service_type}</Text>
                              </div>
                            )}
                          </Group>
                          {request.counter_offer.special_notes && (
                            <div>
                              <Text size="sm" c="dimmed">Princess's Notes</Text>
                              <Text>{request.counter_offer.special_notes}</Text>
                            </div>
                          )}
                        </Stack>
                      </Paper>
                    )}

                    {request.is_expired && (
                      <Alert color="orange" icon={<IconClock size="1rem" />}>
                        The princess did not respond within 72 hours, so this
                        request expired and left her queue. You can send it
                        again with the same details.
                      </Alert>
                    )}

                    {!request.is_expired
                      && request.expires_at
                      && ['pending', 'counter_offered'].includes(request.status)
                      && formatCountdown(request.expires_at) && (
                      <Text size="sm" c="dimmed">
                        <IconClock size="0.8rem" style={{ verticalAlign: 'middle' }} />
                        {' '}{formatCountdown(request.expires_at)}
                      </Text>
                    )}

                    {request.status === 'accepted' && (
                      <Alert color="green" icon={<IconCheck size="1rem" />}>
                        Your request has been accepted! The princess will contact you soon.
                      </Alert>
                    )}

                    {request.status === 'rejected' && (
                      <Alert color="red" icon={<IconX size="1rem" />}>
                        Your request was declined by the princess.
                      </Alert>
                    )}

                    <Divider />

                    <Group>
                      {request.is_expired && (
                        <Button
                          leftSection={<IconRefresh size="1rem" />}
                          color="pink"
                          onClick={() => handleResubmitRequest(request.id)}
                        >
                          Send Again
                        </Button>
                      )}
                      {!request.is_expired && request.status === 'counter_offered' && (
                        <>
                          <Button
                            leftSection={<IconCheck size="1rem" />}
                            color="green"
                            onClick={() => handleAcceptCounterOffer(request.id)}
                          >
                            Accept Counter Offer
                          </Button>
                          <Button
                            leftSection={<IconX size="1rem" />}
                            color="red"
                            variant="light"
                            onClick={() => handleDeclineCounterOffer(request.id)}
                          >
                            Decline Counter Offer
                          </Button>
                          <Button
                            variant="subtle"
                            onClick={() => viewCounterOffer(request)}
                          >
                            View Details
                          </Button>
                        </>
                      )}
                      {/* Cancel is shown for pending requests only as a UX
                          choice — the API (contract §4.6) allows cancel from
                          any status except completed. */}
                      {!request.is_expired && request.status === 'pending' && (
                        <Button
                          leftSection={<IconX size="1rem" />}
                          color="gray"
                          variant="light"
                          onClick={() => handleCancelRequest(request.id)}
                        >
                          Cancel Request
                        </Button>
                      )}
                      {request.status === 'accepted' && (
                        <Button
                          onClick={() => handleMessagePrincess(request.princess_id)}
                        >
                          Message Princess
                        </Button>
                      )}
                    </Group>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>

        <Modal
          opened={viewCounterOfferModal}
          onClose={() => setViewCounterOfferModal(false)}
          title="Counter Offer Details"
          size="lg"
        >
          {selectedRequest && (
            <Stack>
              <Title order={4}>Original Request</Title>
              <Paper p="md" withBorder>
                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">Your Offer</Text>
                    <Text fw={500}>{formatUsd(selectedRequest.offer_price_cents)}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text fw={500}>{selectedRequest.number_of_pictures}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Service</Text>
                    <Text fw={500}>{selectedRequest.service_type}</Text>
                  </div>
                </Group>
              </Paper>

              <Title order={4}>Princess's Counter Offer</Title>
              <Paper p="md" withBorder bg="pink.0">
                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">New Price</Text>
                    <Text fw={600} size="lg" c="pink">
                      {formatUsd(selectedRequest.counter_offer?.offer_price_cents ?? selectedRequest.offer_price_cents)}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text fw={500}>
                      {selectedRequest.counter_offer?.number_of_pictures ?? selectedRequest.number_of_pictures}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Service</Text>
                    <Text fw={500}>
                      {selectedRequest.counter_offer?.service_type || selectedRequest.service_type}
                    </Text>
                  </div>
                </Group>
                {selectedRequest.counter_offer?.special_notes && (
                  <div style={{ marginTop: '1rem' }}>
                    <Text size="sm" c="dimmed">Princess's Notes</Text>
                    <Text>{selectedRequest.counter_offer.special_notes}</Text>
                  </div>
                )}
              </Paper>

              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setViewCounterOfferModal(false)}>
                  Close
                </Button>
                <Button
                  leftSection={<IconCheck size="1rem" />}
                  color="green"
                  onClick={() => {
                    handleAcceptCounterOffer(selectedRequest.id);
                    setViewCounterOfferModal(false);
                  }}
                >
                  Accept Counter Offer
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Container>
    </ProtectedRoute>
  );
};

export default MySpecialRequestsPage;
